import React from 'react';
import { Controller, type Control, type FieldValues } from 'react-hook-form';
import type { DisplayFormat } from '@packages/core';
import { FormField } from '@packages/ui/patterns/FormField';
import { Lozenge, Typography, type LozengeShape, type LozengeSize, type LozengeVariant } from '@packages/ui/primitives';
import { rhfInlineError } from '@packages/ui/hook-form-adapters/rhfInlineError';

interface BaseAdapterProps {
  control: Control<FieldValues>;
  name: string;
  label: string;
  hint?: string;
  required?: boolean;
  semanticId?: string;
  disabled?: boolean;
}

/** Read-only prose display adapter. */
export function RhfProseField({
  control,
  name,
  label,
  hint,
  required,
  semanticId,
}: BaseAdapterProps): React.ReactElement {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => (
        <FormField label={label} hint={hint} required={required} semanticId={semanticId ?? name}>
          <Typography variant='body'>{field.value != null ? String(field.value) : '-'}</Typography>
        </FormField>
      )}
    />
  );
}

/** Display-only label adapter (no input).
 *  Supports `adapterProps` from orch/domain for styling:
 *  - `variant`  – Typography variant (default `'body-s'`)
 *  - `style`    – inline CSS applied to the text element
 *  - `hideLabel`– when true, skip the FormField wrapper (table-each context)
 *  - `lozengeShape` / `lozengeSize` – pill sizing when `displayFormat` is `'badge'` (renders DS Lozenge)
 */
export function RhfLabelField({
  control,
  name,
  label,
  semanticId,
  hideLabel,
  variant,
  style,
  displayFormat,
  badgeVariant,
  badgeTemplate,
  lozengeShape,
  lozengeSize,
  inStack,
}: BaseAdapterProps & {
  hideLabel?: boolean;
  variant?: string;
  style?: React.CSSProperties;
  displayFormat?: DisplayFormat;
  badgeVariant?: string;
  badgeTemplate?: string;
  lozengeShape?: LozengeShape;
  lozengeSize?: LozengeSize;
  inStack?: boolean;
}): React.ReactElement {
  const typoVariant = (variant ?? 'body-s') as React.ComponentProps<typeof Typography>['variant'];
  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => {
        const rawValue = field.value != null && String(field.value).trim() !== '' ? String(field.value) : null;
        const content =
          displayFormat === 'badge' && rawValue != null ? (
            <Lozenge
              variant={(badgeVariant ?? 'primary') as LozengeVariant}
              shape={lozengeShape ?? 'pill'}
              size={lozengeSize ?? 'sm'}
              semanticId={semanticId ?? name}
            >
              {badgeTemplate ? badgeTemplate.replace('{value}', rawValue) : rawValue}
            </Lozenge>
          ) : (
            <Typography variant={typoVariant} style={style}>
              {rawValue ?? label}
            </Typography>
          );

        if (displayFormat === 'badge' && rawValue == null) {
          return null;
        }

        if (hideLabel) {
          return (
            <div
              style={
                inStack
                  ? { display: 'flex', alignItems: 'center' }
                  : { display: 'flex', alignItems: 'center', height: '100%', minHeight: 36 }
              }
            >
              {content}
            </div>
          );
        }
        return (
          <FormField label={label} semanticId={semanticId ?? name}>
            {content}
          </FormField>
        );
      }}
    />
  );
}

function PlaceholderAdapter({
  control,
  name,
  label,
  hint,
  required,
  semanticId,
  adapterName,
}: BaseAdapterProps & { adapterName: string }): React.ReactElement {
  return (
    <Controller
      control={control}
      name={name}
      render={({ fieldState, formState }) => {
        const errorText = rhfInlineError(fieldState, { isSubmitted: formState.isSubmitted });
        return (
          <FormField
            label={label}
            hint={hint ?? `${adapterName} adapter — register a custom implementation to replace this placeholder.`}
            required={required}
            semanticId={semanticId ?? name}
            error={errorText}
          >
            <Typography variant='body-s' color='var(--ui-text-muted)'>
              [{adapterName}] placeholder
            </Typography>
          </FormField>
        );
      }}
    />
  );
}

export function RhfMonacoField(props: BaseAdapterProps): React.ReactElement {
  return <PlaceholderAdapter {...props} adapterName='monaco' />;
}

export function RhfJsonField(props: BaseAdapterProps): React.ReactElement {
  return <PlaceholderAdapter {...props} adapterName='json' />;
}

export function RhfMultiSelectPanel(props: BaseAdapterProps): React.ReactElement {
  return <PlaceholderAdapter {...props} adapterName='multi-select-panel' />;
}
